import { useAtom } from 'jotai/index';
import {
  deletingConfigurationLoadingAtom,
  gettingConfigurationsLoadingAtom,
  configurationsAtom,
  configurationsPagesInfoAtom,
  editingConfigurationLoadingAtom,
  configurationsProjectAtom,
  configurationsItemsOnPageAtom,
  downloadingFileLoadingAtom, configurationsSearchQueryAtom,
} from '../atoms/configurationsAtoms';
import sendRequest from '../helpers/sendRequest';
import Api from '../api';
import {
  closeModalAtom, modalStateAtom, openConfirmModalAtom, openInfoModalAtom, openLoadingModalAtom,
} from '../atoms/modalAtoms';
import useTranslations from './useTranslations';
import { DICTIONARY } from '../translations/dictionary';
import AddToProjectModal from '../pages/Configurations/components/AddToProjectModal';
import { configuratorSingleConfigurationAtom } from '../atoms/configuratorAtoms';
import { api } from '../api/configs';
import FilesServices from '../services/FilesServices';
import { isAdminAtom } from '../atoms/accountAtoms';
import { projectsAtom } from '../atoms/projectsAtoms';
import useTutorials from './useTutorials';

// Only the latest list request may update the list (a request sent before a language change can come back later).
let configurationsListRequestId = 0;

const useConfigurations = ({ setSelectedItems } = {}) => {
  const { t } = useTranslations();
  const { changeTutorialStep, tutorialStep } = useTutorials();

  const [isAdmin] = useAtom(isAdminAtom);
  const [configurations, setConfigurations] = useAtom(configurationsAtom);
  const [gettingConfigurationsLoading, setGettingConfigurationsLoading] = useAtom(gettingConfigurationsLoadingAtom);
  const [editingConfigurationLoading, setEditingConfigurationLoading] = useAtom(editingConfigurationLoadingAtom);
  const [deletingConfigurationLoading, setDeletingConfigurationLoading] = useAtom(deletingConfigurationLoadingAtom);
  const [downloadingFileLoading, setDownloadingFileLoading] = useAtom(downloadingFileLoadingAtom);
  const [configurationsPagesInfo, setConfigurationsPagesInfo] = useAtom(configurationsPagesInfoAtom);
  const [configurationsProject, setConfigurationsProject] = useAtom(configurationsProjectAtom);
  const [configurationsItemsOnPage] = useAtom(configurationsItemsOnPageAtom);
  const [configuratorSingleConfiguration, setConfiguratorSingleConfiguration] = useAtom(configuratorSingleConfigurationAtom);
  const [, setProjects] = useAtom(projectsAtom);
  const [configurationsSearchQuery, setProjectsSearchQuery] = useAtom(configurationsSearchQueryAtom);

  const [, setModalState] = useAtom(modalStateAtom);
  const [, openConfirmModal] = useAtom(openConfirmModalAtom);
  const [, openInfoModal] = useAtom(openInfoModalAtom);
  const [, openLoadingModal] = useAtom(openLoadingModalAtom);
  const [, closeModal] = useAtom(closeModalAtom);

  const searchConfigurations = (query) => {
    const trimmed = query.trim();
    setProjectsSearchQuery(trimmed);
    setSelectedItems?.([]);
  };

  // itemsOnPage comes from the desktop screen height. Without it the server default is used.
  const getConfigurations = async ({
    page, search, shouldAddToExisting, itemsOnPage,
  }) => {
    const payload = { page, items_on_page: itemsOnPage || configurationsItemsOnPage };
    if (search) payload.search = search;

    configurationsListRequestId += 1;
    const requestId = configurationsListRequestId;
    // Loading ends with the latest request only, so an older one finishing first does not hide the loader.
    setGettingConfigurationsLoading(true);
    const data = await sendRequest({
      request: Api.getConfigurations,
      payload,
    });

    if (requestId !== configurationsListRequestId) return;
    setGettingConfigurationsLoading(false);
    if (data?.status !== 'ok') return;

    if (page !== 1 && page !== '1' && shouldAddToExisting) setConfigurations((prev) => [...prev, ...data.data.configurations]);
    else setConfigurations(data.data.configurations);

    setConfigurationsPagesInfo(data.data.pages);
  };

  const getConfigurationsOfProject = async ({
    page, projectId, shouldAddToExisting, itemsOnPage,
  }) => {
    configurationsListRequestId += 1;
    const requestId = configurationsListRequestId;
    if (!shouldAddToExisting) setGettingConfigurationsLoading(true);
    const data = await sendRequest({
      request: Api.getConfigurationsOfProject,
      payload: { id: projectId, params: { page, items_on_page: itemsOnPage || configurationsItemsOnPage } },
    });

    if (requestId !== configurationsListRequestId) return;
    setGettingConfigurationsLoading(false);
    if (data?.status !== 'ok') {
      // A failed switch must not keep the previous project on screen.
      if (!configurationsProject || String(configurationsProject.id) !== String(projectId)) {
        setConfigurationsProject({ id: projectId, name: '' });
        setConfigurations([]);
        setConfigurationsPagesInfo(null);
      }
      return;
    }

    const { project } = data.data;
    if (page !== 1 && page !== '1' && shouldAddToExisting) setConfigurations((prev) => [...prev, ...project.configurations.list]);
    else setConfigurations(project.configurations.list);

    setConfigurationsPagesInfo(project.configurations.pages);
    setConfigurationsProject({ ...project, id: project.id ?? projectId, configurations: undefined });
  };

  const editConfiguration = (configuration, cb) => async ({ name, description }) => {
    if (editingConfigurationLoading) return;

    // project_id is not sent: POST /account/configurations with project_id copies the configuration.
    // Adding to projects goes through attachConfigurationsToProjects.
    const body = { id: configuration.id };
    if (name) body.name = name;
    if (description) body.description = description;

    const data = await sendRequest({
      request: Api.editConfiguration,
      payload: body,
      setLoading: setEditingConfigurationLoading,
    });

    if (data?.status === 'ok') {
      const { configuration: newConfiguration } = data.data;
      cb?.(newConfiguration);
      setConfigurations((prev) => prev.map((i) => (i.id === newConfiguration.id ? ({ ...i, ...newConfiguration }) : i)));
      closeModal();
    }

    return data;
  };

  const addConfigurationsToProjects = (configurations) => async (projects) => {
    if (editingConfigurationLoading) return;
    if (!configurations?.length || !projects?.length) return;

    const configurationIds = [];
    configurations.forEach((configuration) => {
      if (configuration?.id == null) return;
      if (configurationIds.some((id) => String(id) === String(configuration.id))) return;
      configurationIds.push(configuration.id);
    });

    const projectIds = [];
    projects.forEach((project) => {
      if (project?.id == null) return;
      if (projectIds.some((id) => String(id) === String(project.id))) return;
      projectIds.push(project.id);
    });

    if (!configurationIds.length || !projectIds.length) return;

    // Each pair becomes its own copy. History originals stay as they are.
    // Copy ids are only on GET /account/projects/{id}; this screen does not need them.
    const data = await sendRequest({
      request: Api.attachConfigurationsToProjects,
      payload: {
        projects: projectIds,
        configurations: configurationIds,
      },
      setLoading: setEditingConfigurationLoading,
    });

    if (data?.status !== 'ok') return;

    const updatedProjects = Array.isArray(data.data?.projects)
      ? data.data.projects
      : Array.isArray(data.data)
        ? data.data
        : [];

    if (updatedProjects.length) {
      setProjects((prev) => prev.map((project) => {
        const updated = updatedProjects.find((item) => String(item.id) === String(project.id));
        if (!updated || updated.configurations_count == null) return project;
        return { ...project, configurations_count: updated.configurations_count };
      }));
    }

    openInfoModal({
      text: t(DICTIONARY.configurationsAddedToProjects, configurationIds.length, projectIds.length),
    });
  };

  const detachConfigurations = async (projectId, listOfIds) => {
    if (deletingConfigurationLoading) return;

    const data = await sendRequest({
      request: Api.detachConfigurations,
      payload: { id: projectId, configurations: listOfIds },
      setLoading: setDeletingConfigurationLoading,
    });

    if (data?.status === 'ok') {
      setSelectedItems([]);
      setConfigurations((prev) => prev.map((i) => (listOfIds.includes(i.id) ? { ...i, project_id: 0 } : i)));
      setConfigurationsPagesInfo((prev) => ({ ...prev, total_records: prev.total_records - listOfIds.length }));
      setConfigurations((prev) => prev.filter((i) => !listOfIds.includes(i.id)));
      closeModal();
    }
  };

  const deleteConfigurations = async (listOfIds, cb) => {
    if (deletingConfigurationLoading) return;

    const data = await sendRequest({
      request: Api.deleteConfigurations,
      payload: { ids: listOfIds },
      setLoading: setDeletingConfigurationLoading,
    });

    if (data?.status === 'ok') {
      setSelectedItems([]);
      setConfigurationsPagesInfo((prev) => ({ ...prev, total_records: prev.total_records - listOfIds.length }));
      setConfigurations((prev) => prev.filter((i) => !listOfIds.includes(i.id)));
      cb?.();
      closeModal();
    }
  };

  /* Files Start */
  const downloadConfigurationsArchive = async (ids) => {
    if (downloadingFileLoading) return;

    if (tutorialStep === 9) {
      return changeTutorialStep();
    }

    openLoadingModal();
    const body = { ids };

    const response = await sendRequest({
      request: Api.generateConfigurationsArchive,
      payload: body,
      setLoading: setDownloadingFileLoading,
      getFullResponse: true,
    });

    const { data, headers } = response || {};

    let isError = false;
    if (data?.status !== 'failed') {
      const downloadRes = FilesServices.downloadPdf(data, headers);
      isError = downloadRes.isError;
    }

    if (!isError) closeModal();
  };

  const downloadConfigurationsPdf = async (ids) => {
    if (downloadingFileLoading) return;

    openLoadingModal();
    const body = { ids };

    const response = await sendRequest({
      request: Api.generateConfigurationsPdf,
      payload: body,
      setLoading: setDownloadingFileLoading,
      getFullResponse: true,
    });

    const { data, headers } = response || {};

    let isError = false;
    if (data?.status !== 'failed') {
      const downloadRes = FilesServices.downloadPdf(data, headers);
      isError = downloadRes.isError;
    }

    if (!isError) closeModal();
  };

  const downloadConfigurationsDocx = async (ids) => {
    if (downloadingFileLoading) return;

    openLoadingModal();
    const body = { ids };

    const response = await sendRequest({
      request: Api.generateConfigurationsDocx,
      payload: body,
      setLoading: setDownloadingFileLoading,
      getFullResponse: true,
    });

    const { data, headers } = response || {};

    let isError = false;
    if (data?.status !== 'failed') {
      const downloadRes = FilesServices.downloadDocx(data, headers);
      isError = downloadRes.isError;
    }

    if (!isError) closeModal();
  };

  const downloadConfigurationsFile = async (ids) => {
    if (isAdmin && false) await downloadConfigurationsDocx(ids);
    else await downloadConfigurationsPdf(ids);
  };

  const emailConfigurationsArchive = async (ids) => {
    if (downloadingFileLoading) return;

    if (tutorialStep === 10) {
      return changeTutorialStep();
    }

    openLoadingModal();
    const body = { ids, return_to: 'mail' };

    const data = await sendRequest({
      request: Api.generateConfigurationsArchive,
      payload: body,
      setLoading: setDownloadingFileLoading,
    });

    if (data?.status !== 'failed') {
      openInfoModal({ text: data.message || t(DICTIONARY.emailSuccess) });
    } else {
      closeModal();
    }
  };
  /* Files End */

  const handleConfigurationDescription = async (val) => {
    if (!configuratorSingleConfiguration?.id) return;
    await editConfiguration(configuratorSingleConfiguration, (newConfiguration) => {
      const newObj = { name: newConfiguration.name, project_id: newConfiguration.project_id, description: newConfiguration.description };
      setConfiguratorSingleConfiguration((prev) => ({ ...prev, ...newObj }));
    })({ description: val });
  };

  const onAddToProjectClick = (configurationOrList) => {
    if (tutorialStep === 11) {
      changeTutorialStep();
    }

    const configurations = (Array.isArray(configurationOrList) ? configurationOrList : [configurationOrList])
      .filter((item) => item?.id);

    setModalState({
      isOpen: true,
      withCloseButton: true,
      children: (
        <AddToProjectModal
          configurations={configurations}
          onSave={addConfigurationsToProjects(configurations)}
        />
      ),
    });
  };

  const onDetachConfigurationsClick = (projectId, listOfIds) => {
    openConfirmModal({
      text: listOfIds.length === 1 ? t(DICTIONARY.sureToDetachConfiguration) : t(DICTIONARY.sureToDetachConfigurations, listOfIds.length),
      onButtonClick: () => detachConfigurations(projectId, listOfIds),
    });
  };

  const onDeleteConfigurationsClick = (listOfIds, cb) => {
    openConfirmModal({
      text: listOfIds.length === 1 ? t(DICTIONARY.sureToDeleteConfiguration) : t(DICTIONARY.sureToDeleteConfigurations, listOfIds.length),
      onButtonClick: () => deleteConfigurations(listOfIds, cb),
    });
  };

  return {
    configurations,
    gettingConfigurationsLoading,
    editingConfigurationLoading,
    configurationsPagesInfo,
    configurationsProject,
    configurationsSearchQuery,
    getConfigurations,
    searchConfigurations,
    getConfigurationsOfProject,
    onAddToProjectClick,
    onDeleteConfigurationsClick,
    onDetachConfigurationsClick,
    handleConfigurationDescription,
    downloadConfigurationsArchive,
    downloadConfigurationsFile,
    emailConfigurationsArchive,
  };
};

export default useConfigurations;
